import { Edit2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../../components/ui/ThreeDotsMenu";
import { formatDate } from "../../../lib/utils";
import { Fine, FineSearchRequest, FineSearchResponse } from "../@types";
import { useGetFinesMutation } from "../fineApiSlice";
import FineModal from "../FineModal";
import DeleteFineModal from "../ui/DeleteFine";

const FinesDashTab = () => {
  const [showModal, setShowModal] = useState("");
  const [selectedFine, setSelectedFine] = useState<Fine | null>(null);
  const [getFines, { data: finesData, isLoading, error: loadError }] = useGetFinesMutation();

  const fetchFines = useCallback(async () => {
    const requestBody: FineSearchRequest = {
      pageNumber: 0,
      pageSize: 100,
    };

    try {
      await getFines(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch fines:", err);
    }
  }, [getFines]);

  useEffect(() => {
    fetchFines();
  }, [fetchFines]);

  // Table columns configuration
  const columns: Column<Fine>[] = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      render: (value: number) => (
        <span className="text-hColor">{value?.toLocaleString()}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "createdOn",
      header: "Created on",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Fine) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setShowModal("edit");
                  setSelectedFine(item);
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setShowModal("delete");
                  setSelectedFine(item);
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const response = finesData?.data as FineSearchResponse | undefined;

  return (
    <div>
      <Table
        data={response?.data || []}
        columns={columns}
        loading={isLoading}
        error={loadError}
        emptyMessage="No fine found"
      />

      <FineModal
        isOpen={showModal === "edit"}
        onClose={() => {
          setShowModal("");
          setSelectedFine(null);
          fetchFines();
        }}
        fine={selectedFine}
        mode="edit"
      />

      {selectedFine && (
        <DeleteFineModal
          fine={selectedFine}
          isOpen={showModal === "delete"}
          onClose={() => {
            setShowModal("");
            setSelectedFine(null);
            fetchFines();
          }}
        />
      )}
    </div>
  );
};

export default FinesDashTab;
